import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../ui/Button';
import { FiCheckCircle, FiCircle, FiLock } from 'react-icons/fi';

export default function InteractiveDemo({ onAuthOpen }) {
  const [demoTasks, setDemoTasks] = useState([
    { id: 1, text: 'Günün ilk hedefini belirle', completed: false },
    { id: 2, text: '45 Dakika odaklanma seansı yap', completed: false },
    { id: 3, text: 'Liderlik tablosunda yerini al', completed: false, locked: true },
  ]);
  const [demoXp, setDemoXp] = useState(0);
  const [showXpAnim, setShowXpAnim] = useState(false);

  const handleCompleteTask = (taskId) => {
    const task = demoTasks.find((t) => t.id === taskId);
    if (task.locked) {
      onAuthOpen();
      return;
    }
    if (task.completed) return;

    setDemoTasks(demoTasks.map((t) => (t.id === taskId ? { ...t, completed: true } : t)));
    setDemoXp((prev) => prev + 50);
    
    // Trigger animation
    setShowXpAnim(true);
    setTimeout(() => setShowXpAnim(false), 2000);
  };

  return (
    <section className="interactive-demo" style={{ padding: 'var(--space-12) 0', position: 'relative' }}>
      <div className="gradient-blob gradient-blob--indigo animate-blob" style={{ width: 400, height: 400, right: '-10%', top: '0', opacity: 0.15 }} />
      
      <div className="section-header" style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }}>
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Hemen Şimdi Dene
        </motion.h2>
        <p className="text-secondary" style={{ fontSize: 'var(--text-lg)' }}>
          Kayıt olmadan önce hedeflerini tamamlamanın nasıl hissettirdiğini gör!
        </p>
      </div>

      <div className="demo-container" style={{ maxWidth: '500px', margin: '0 auto', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-6)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-lg)', position: 'relative' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 600 }}>Günlük Görevlerin</h3>
          <div style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)', padding: '4px 12px', borderRadius: 'var(--radius-full)', fontWeight: 700 }}>
            {demoXp} XP
          </div>
        </div>

        <div className="demo-tasks" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {demoTasks.map((task) => (
            <motion.div
              key={task.id}
              className={`demo-task-item ${task.completed ? 'completed' : ''} ${task.locked ? 'locked' : ''}`}
              whileHover={{ scale: task.completed ? 1 : 1.02 }}
              whileTap={{ scale: task.completed ? 1 : 0.98 }}
              onClick={() => handleCompleteTask(task.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3)',
                background: task.completed ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-tertiary)',
                border: `1px solid ${task.completed ? 'var(--color-success)' : 'var(--border-subtle)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: task.completed ? 'default' : 'pointer',
                opacity: task.locked ? 0.7 : 1,
                transition: 'all 0.3s ease'
              }}
            >
              {task.locked ? (
                <FiLock size={20} color="var(--text-muted)" />
              ) : task.completed ? (
                <FiCheckCircle size={20} color="var(--color-success)" />
              ) : (
                <FiCircle size={20} color="var(--text-secondary)" />
              )}
              
              <span style={{ 
                flex: 1, 
                color: task.completed ? 'var(--color-success)' : 'var(--text-primary)',
                textDecoration: task.completed ? 'line-through' : 'none'
              }}>
                {task.text}
              </span>
              
              {!task.locked && !task.completed && (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--accent-primary)', fontWeight: 600 }}>
                  +50 XP
                </span>
              )}
            </motion.div>
          ))}
        </div>

        {/* XP Gain Animation Overlay */}
        <AnimatePresence>
          {showXpAnim && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5, y: 0 }}
              animate={{ opacity: 1, scale: 1.5, y: -50 }}
              exit={{ opacity: 0, scale: 2, y: -100 }}
              style={{
                position: 'absolute', top: '40%', left: '40%',
                color: 'var(--accent-primary)', fontSize: '24px', fontWeight: 800,
                textShadow: '0 0 20px var(--accent-glow-strong)', pointerEvents: 'none'
              }}
            >
              +50 XP!
            </motion.div>
          )}
        </AnimatePresence>

        {demoXp > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ marginTop: 'var(--space-6)', textAlign: 'center', padding: 'var(--space-4)', background: 'var(--bg-glass-strong)', borderRadius: 'var(--radius-md)' }}
          >
            <p style={{ marginBottom: 'var(--space-3)' }}>Harika! <strong style={{ color: 'var(--accent-primary)' }}>{demoXp} XP</strong> kazandın.</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 'var(--space-3)' }}>Bu XP'leri hesabına aktarmak ve sıralamaya girmek için hemen kayıt ol.</p>
            <Button variant="primary" style={{ width: '100%' }} onClick={onAuthOpen}>
              Kazandığım XP'leri Kaydet
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
}
